import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Clock, ShieldCheck } from "lucide-react";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import SectionAtmosphere from "@/components/SectionAtmosphere";
import CheckinSimulator from "@/components/marketing/CheckinSimulator";
import { Button } from "@/components/ui/button";
import atmosphereDemo from "@/assets/atmosphere-features.jpg";
import { lqipFor } from "@/generated/atmospheric-lqip";
import { useCanonical } from "@/lib/useCanonical";

const META_TITLE = "Fictional retrospective check-in demo | Myelina Health";
const META_DESCRIPTION =
  "A local-only fictional demonstration of organizing self-reported context for retrospective review. No prediction, score, recommendation, email collection, account, or stored response.";

const setMeta = (name: string, content: string, attr: "name" | "property" = "name") => {
  let element = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attr, name);
    document.head.appendChild(element);
  }
  element.setAttribute("content", content);
  return element;
};

const Demo = () => {
  useCanonical({ path: "/demo" });

  useEffect(() => {
    const previousTitle = document.title;
    const description = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
    const robots = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
    const ogTitle = document.querySelector('meta[property="og:title"]') as HTMLMetaElement | null;
    const ogDescription = document.querySelector('meta[property="og:description"]') as HTMLMetaElement | null;
    const previousDescription = description?.getAttribute("content") ?? null;
    const previousRobots = robots?.getAttribute("content") ?? null;
    const previousOgTitle = ogTitle?.getAttribute("content") ?? null;
    const previousOgDescription = ogDescription?.getAttribute("content") ?? null;

    document.title = META_TITLE;
    const descriptionElement = setMeta("description", META_DESCRIPTION);
    const robotsElement = setMeta("robots", "noindex, follow");
    const ogTitleElement = setMeta("og:title", META_TITLE, "property");
    const ogDescriptionElement = setMeta(
      "og:description",
      META_DESCRIPTION,
      "property",
    );

    return () => {
      document.title = previousTitle;
      if (previousDescription === null) descriptionElement.remove();
      else descriptionElement.setAttribute("content", previousDescription);
      if (previousRobots === null) robotsElement.remove();
      else robotsElement.setAttribute("content", previousRobots);
      if (previousOgTitle === null) ogTitleElement.remove();
      else ogTitleElement.setAttribute("content", previousOgTitle);
      if (previousOgDescription === null) ogDescriptionElement.remove();
      else ogDescriptionElement.setAttribute("content", previousOgDescription);
    };
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main id="main-content">
        <SectionAtmosphere
          ariaLabelledBy="demo-hero-heading"
          src={atmosphereDemo}
          optimizedBase="/opt/atmosphere-features"
          lqip={lqipFor(atmosphereDemo)}
          origin="top right"
          intensity={0.13}
          glow="right"
          tone="cream"
          className="pt-40 pb-14 sm:pt-48 sm:pb-18"
        >
          <div className="container mx-auto px-6 sm:px-10 lg:px-16">
            <div className="mx-auto max-w-3xl space-y-7 text-center">
              <div className="eyebrow justify-center mx-auto">
                <Clock className="h-3 w-3" aria-hidden="true" />
                Fictional · local-only · retrospective
              </div>
              <h1 id="demo-hero-heading" className="font-display text-display-xl text-foreground">
                Three questions. <span className="italic text-primary">One retrospective view.</span>
              </h1>
              <p className="lead-italic mx-auto max-w-2xl">
                Try a fictional reflection that organizes what you report about sleep, present body state, and demands already experienced. It does not predict, score, diagnose, recommend, or collect contact information.
              </p>
              <div className="mx-auto flex max-w-2xl items-start gap-3 rounded-2xl border border-primary/20 bg-background/75 p-4 text-left text-sm leading-6 text-foreground/80">
                <ShieldCheck className="mt-0.5 h-5 w-5 flex-none text-primary" aria-hidden="true" />
                <p>
                  Selections remain in browser memory for this page session. They are not submitted to Myelina, written to a database, used for outreach, or connected to an account.
                </p>
              </div>
            </div>
          </div>
        </SectionAtmosphere>

        <section className="relative bg-background pb-24 sm:pb-32" aria-label="Local retrospective demonstration">
          <div className="container mx-auto px-6 sm:px-10 lg:px-16">
            <CheckinSimulator variant="page" />
          </div>
        </section>

        <section className="relative border-t border-border/40 bg-card py-20 sm:py-24" aria-labelledby="demo-boundary-heading">
          <div className="container mx-auto max-w-2xl px-6 text-center sm:px-10 lg:px-16">
            <h2 id="demo-boundary-heading" className="font-display text-3xl tracking-tight text-foreground sm:text-4xl">
              The demonstration ends here.
            </h2>
            <p className="mt-4 text-[15px] leading-7 text-foreground/80">
              This page does not request an email, create an account, offer an invitation, enroll a participant, or represent a production clinical workflow. Public collection and account creation remain paused while evidence is reviewed.
            </p>
            <Button asChild variant="outline" size="lg" className="mt-7">
              <Link to="/">Return to the public overview</Link>
            </Button>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Demo;
